import type { BuildGameInterviewState } from '@volley/crucible-ipc-types/build-game-types';

/**
 * Pure-render Game Design Summary. Used by three surfaces:
 *
 *   1. The chat-panel resume summary (when a Workspace mounts and
 *      finds an in-progress interview).
 *   2. The review-and-edit modal (`<ReviewAnswersModal>`).
 *   3. The pre-build confirmation gate (after Phase A completes,
 *      before subagents are dispatched).
 *
 * Rendering rules from the plan doc:
 *
 *   - Group answers by agent (game-designer, product-manager, ...).
 *   - Show progress per-agent ("4/9 answered").
 *   - Visually mark inferred-but-not-confirmed answers with an
 *     orange dot — `_inferred && !confirmedAt`. Once confirmed,
 *     marker drops.
 *   - Skipped questions render in muted grey, not as missing.
 *   - When an `onEdit` callback is supplied, each answered question
 *     gets an Edit button; otherwise the surface is read-only
 *     (resume panel + pre-build gate render that way).
 */

export interface GameDesignSummaryProps {
  state: BuildGameInterviewState;
  /**
   * When supplied, each row gets an Edit button that calls back with
   * the agent + question id. The review-and-edit modal sets this;
   * the resume panel and pre-build gate leave it undefined for a
   * read-only render.
   */
  onEdit?: (args: { agent: string; questionId: string }) => void;
}

export function GameDesignSummary({
  state,
  onEdit,
}: GameDesignSummaryProps): React.JSX.Element {
  // Crucible #570: `state.agents` is now optional — the SKILL's
  // quick-batch plan-mode state has no engine fields. This summary
  // only renders for canonical engine-written states, but defend
  // against the empty case anyway so a partial read doesn't crash
  // the page.
  const agents = state.agents ?? {};
  const agentNames = Object.keys(agents);
  return (
    <div data-testid="game-design-summary" className="flex flex-col gap-4">
      {agentNames.map((agentName) => {
        const block = agents[agentName]!;
        // Crucible #936: `block.questions` is the wire shape from
        // `build-game-interview.json`. Intermediate SKILL bootstrap
        // states (hw#206 quick-batch path) can land an
        // object-keyed-by-id rather than an array — the canonical
        // engine writer normalises it back to an array on the next
        // write, but a renderer read between the two crashes
        // catastrophically (`H.map is not a function` in the
        // production bundle). The main-side
        // `build-game-state.ts:231` `interviewStatusSummary`
        // already guards the same shape with `Array.isArray`; this
        // is the matching renderer guard.
        const questions = Array.isArray(block.questions) ? block.questions : [];
        const answered = questions.filter(
          (q) => (q.answer !== undefined && q.answer !== '') || q.skippedAt !== undefined,
        ).length;
        return (
          <section
            key={agentName}
            data-testid={`gds-agent-${agentName}`}
            className="rounded-lg border border-white/10 bg-white/5 p-3"
          >
            <header className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-white/80">
                {humaniseAgentName(agentName)}
              </h3>
              <span className="text-xs text-white/40">
                {answered}/{questions.length} answered
              </span>
            </header>
            <ol className="flex flex-col gap-2">
              {questions.map((q) => {
                const isAnswered = q.answer !== undefined && q.answer !== '';
                const isSkipped = q.skippedAt !== undefined;
                const isInferredUnconfirmed = q._inferred === true && !q.confirmedAt;
                return (
                  <li
                    key={q.id}
                    data-testid={`gds-question-${q.id}`}
                    data-inferred-unconfirmed={isInferredUnconfirmed ? 'true' : 'false'}
                    className="text-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-white/60">
                          {isInferredUnconfirmed && (
                            <span
                              title="Inferred from your pitch — confirm or change"
                              data-testid="gds-inferred-marker"
                              className="h-1.5 w-1.5 rounded-full bg-amber-400"
                            />
                          )}
                          <span className="text-xs">{q.text}</span>
                        </div>
                        <div className="mt-0.5 text-white/90">
                          {isAnswered && q.answer}
                          {!isAnswered && isSkipped && (
                            <span className="text-white/30 italic">
                              skipped ({q.skipReason ?? 'user'})
                            </span>
                          )}
                          {!isAnswered && !isSkipped && (
                            <span className="text-white/30 italic">— pending —</span>
                          )}
                        </div>
                      </div>
                      {onEdit && isAnswered && (
                        <button
                          onClick={() => onEdit({ agent: agentName, questionId: q.id })}
                          className="shrink-0 rounded border border-white/10 px-2 py-0.5 text-xs text-white/60 hover:bg-white/5"
                          aria-label={`Edit answer for ${q.text}`}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Render `game-designer` as "Game Designer", `principal-engineer`
 * as "Principal Engineer", etc. Each agent's `.md` file uses
 * kebab-case but humans read better with title case.
 */
function humaniseAgentName(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
